'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useState, useEffect, useRef } from 'react'

import SidePanel from "@/components/ui/side-panel"
import Header from "@/components/ui/header"
import { useAuthStore } from '@/stores/auth-store'
import { usePageTitle } from '@/hooks/use-page-title'

interface LayoutClientProps {
  children: React.ReactNode
}

export default function LayoutClient({ children }: LayoutClientProps) {
  // Use custom hook for page title management
  usePageTitle();

  const [mounted, setMounted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pathnameState, setPathname] = useState<string>('')
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false)
  const router = useRouter()
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastRedirectRef = useRef<string>('')
  
  // Handle hydration properly
  useEffect(() => {
    setMounted(true)
  }, [])

  // Only call usePathname after mounting to prevent SSR issues
  const pathnameHook = usePathname() || ''
  
  useEffect(() => {
    if (mounted) {
      setPathname(pathnameHook)
    }
  }, [pathnameHook, mounted])
  
  // Use auth store with error handling
  const authStore = useAuthStore()
  const isAuthenticated = authStore.isAuthenticated
  const user = authStore.user
  const logout = authStore.logout
  const isInitialized = authStore.isInitialized
  const clearAuth = authStore.clearAuth
  
  const hiddenNavPaths = ["/pos", "/invoice-preview", "/receipt-preview", "/receipt-print"]
  const authPaths = ["/login", "/signup", "/forgot-password", "/reset-password"]
  
  // Normalize pathname by removing trailing slash for consistent matching
  const normalizedPathname = pathnameState.replace(/\/$/, '')
  
  const shouldHideNav = hiddenNavPaths.some(path => normalizedPathname.startsWith(path))
  const isAuthPage = authPaths.some(path => normalizedPathname.startsWith(path))
  
  // Handle authentication redirects with debouncing
  useEffect(() => {
    if (mounted && isInitialized) {
      // Clear any existing timeout
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
      }
      
      // Debounce redirects to prevent rapid redirects
      redirectTimeoutRef.current = setTimeout(() => {
        const currentPath = window.location.pathname
        
        // If not authenticated and not on an auth page, redirect to login
        if (!isAuthenticated && !isAuthPage) {
          if (currentPath !== '/login' && lastRedirectRef.current !== '/login') {
            lastRedirectRef.current = '/login'
            router.push('/login');
          }
        }
        // If authenticated and on an auth page, redirect to dashboard
        else if (isAuthenticated && isAuthPage) {
          if (currentPath !== '/' && lastRedirectRef.current !== '/') {
            lastRedirectRef.current = '/'
            router.push('/');
          }
        }
      }, 100) // 100ms debounce
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
      }
    }
  }, [mounted, isInitialized, isAuthenticated, isAuthPage, router])

  // Global error handler for 401 errors
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      // Check if the error is related to a 401 response
      if (event.error && typeof event.error === 'object' && 'response' in event.error) {
        const response = (event.error as any).response;
        if (response && response.status === 401) {
          clearAuth();
        }
      }
    };

    // Add global error listener
    window.addEventListener('error', handleGlobalError);
    
    // Also listen for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && typeof event.reason === 'object' && 'response' in event.reason) {
        const response = (event.reason as any).response;
        if (response && response.status === 401) {
          clearAuth();
        }
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [clearAuth]);

  const toggleSidePanel = () => {
    setIsSidePanelOpen(!isSidePanelOpen)
  }

  const closeSidePanel = () => {
    setIsSidePanelOpen(false)
  }

  // For auth pages, ensure theme context is ready before rendering
  if (isAuthPage) {
    // Show loading state while mounting to ensure theme context is ready
    if (!mounted) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#111517]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d4af37] mx-auto mb-4"></div>
            <p className="text-gray-300">Loading...</p>
          </div>
        </div>
      );
    }
    return <>{children}</>;
  }

  // Show loading while checking authentication
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Full app layout with navigation
  return (
    <div className="flex h-screen bg-background">
      {/* Side Panel */}
      <SidePanel isOpen={isSidePanelOpen} onClose={closeSidePanel} />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <Header onMenuClick={toggleSidePanel} />
        
        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
        

      </div>
    </div>
  )
} 